import React, { useState, useEffect, useRef } from 'react';

const ApiPollingService = ({ isLoggedIn, onPoll, onLogout }) => {
  const intervalId = useRef(null);
  const initialTimeoutId = useRef(null);
  const lastActivityTime = useRef(Date.now());
  const autoLogoutId = useRef(null);

  // Function to check for inactivity
  const checkInactivity = () => {
    const currentTime = Date.now();
    const inactiveTime = currentTime - lastActivityTime.current;
    const fiftyFiveMinutes = 55 * 60 * 1000; // 55 minutes in milliseconds

    if (inactiveTime >= fiftyFiveMinutes) {
      onLogout(); // Trigger logout
    }
  };

  // Track user activity - only when logged in
  useEffect(() => {
    if (!isLoggedIn) return; // Skip if not logged in

    const updateActivity = () => {
      lastActivityTime.current = Date.now();
    };

    // Events to track
    const events = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'mousemove'
    ];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, updateActivity);
    });

    // Cleanup event listeners
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, updateActivity);
      });
    };
  }, [isLoggedIn]); // Added isLoggedIn as dependency

  // Set up polling and auto-logout checking
  useEffect(() => {
    const startPolling = () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }

      intervalId.current = setInterval(() => {
        onPoll();
      }, 55 * 60 * 1000); // 55 minute intervals for API polling
    };

    if (isLoggedIn) {
      // Start polling after initial delay
      initialTimeoutId.current = setTimeout(() => {
        onPoll();
        startPolling();
      }, 55 * 60 * 1000);

      // Start inactivity checker - check every minute
      autoLogoutId.current = setInterval(() => {
        checkInactivity();
      }, 60 * 1000); // Check every 1 minute

      // Reset last activity time when logging in
      lastActivityTime.current = Date.now();
    } else {
      // Clean up all intervals and timeouts when logged out
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
      if (initialTimeoutId.current) {
        clearTimeout(initialTimeoutId.current);
      }
      if (autoLogoutId.current) {
        clearInterval(autoLogoutId.current);
      }
    }

    // Cleanup function
    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
      if (initialTimeoutId.current) {
        clearTimeout(initialTimeoutId.current);
      }
      if (autoLogoutId.current) {
        clearInterval(autoLogoutId.current);
      }
    };
  }, [isLoggedIn, onPoll, onLogout]);

  return null;
};

export default ApiPollingService;